import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

import { GlobalState } from "../../../context/GlobalContext";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { grey } from "@mui/material/colors";
import { BarChart } from "@mui/x-charts/BarChart";

import RenderInputField from "components/Reports/ReusableComponents/RenderInputField/RenderInputField";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import StoreMultiSelect from "./StoreMultiSelect";

import { API, REPORT_MODES, getSeasonMonths } from "../data";
import { buildMrpColumns, fillingColumns, manualColumns } from "./reportColumns";
import {
	buildReportFilterColumns,
	DEFAULT_REPORT_FILTERS,
	MRP_GROUP_COLUMN,
	MULTI_FILTER_KEYS,
} from "./reportFilters";
import {
	KpiTile,
	SectionCard,
	STATUS_ORDER,
	StatusFunnel,
	cardSx,
	formatLakhs,
	formatNumber,
	growthPct,
	round1dp,
	sectionLabelSx,
	toLakhs,
} from "./reportUi";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";

const MONTH_KPI_OPTIONS = [{ key: "revenue", label: "Revenue (NSV)" }];

const MONTH_KPI_UNITS = { revenue: "L" };

const MRP_KPI_OPTIONS = [
	{ key: "nsv", label: "Revenue (NSV)", unit: "L" },
	{ key: "qty", label: "Qty", unit: "" },
	{ key: "opt", label: "OPT", unit: "" },
	{ key: "asp", label: "ASP", unit: "" },
	{ key: "dis", label: "Discount %", unit: "%" },
];

const PlanReport = ({ plan }) => {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();

	const [filters, setFilters] = useState(DEFAULT_REPORT_FILTERS);
	const mode = filters.mode || "FINAL";
	const groupBy = filters.group_by || "category";

	const [options, setOptions] = useState({
		stores: [],
		statuses: STATUS_ORDER,
		grades: [],
		city_tiers: [],
		divisions: [],
		categories: [],
	});

	const [summary, setSummary] = useState({});
	const [fillingRows, setFillingRows] = useState([]);
	const [manualRows, setManualRows] = useState([]);
	const [manualLtlSeason, setManualLtlSeason] = useState(null);
	const [months, setMonths] = useState({});
	const [ltlMonths, setLtlMonths] = useState([]);
	const [monthKpi, setMonthKpi] = useState("revenue");
	const [mrpKpi, setMrpKpi] = useState("nsv");
	const [mrpRows, setMrpRows] = useState([]);
	const [mrpLabels, setMrpLabels] = useState(["Category", ""]);

	const [loading, setLoading] = useState(false);
	const [loadingMrp, setLoadingMrp] = useState(false);

	const showError = useCallback(
		(err) => {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrMsgFromError(err), severity: "error" });
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const params = useMemo(() => {
		const p = { mode };
		MULTI_FILTER_KEYS.forEach((key) => {
			const value = filters[key];
			if (Array.isArray(value) && value.length) p[key] = value.join(",");
		});
		return p;
		// group_by is deliberately excluded — it only re-pivots the MRP section.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode, ...MULTI_FILTER_KEYS.map((key) => filters[key])]);

	const filterColumns = useMemo(() => buildReportFilterColumns(options), [options]);

	// The autocomplete allows clearing; source and grouping must always hold a value.
	const handleFilterChange = useCallback(
		(next) =>
			setFilters({
				...next,
				mode: next.mode || DEFAULT_REPORT_FILTERS.mode,
				group_by: next.group_by || DEFAULT_REPORT_FILTERS.group_by,
			}),
		[]
	);

	const fetchSection = useCallback(
		async (section, extra = {}) => {
			if (!plan?.id) return null;
			const { data } = await axios.get(API.GET_PLAN_REPORT(plan.id), {
				params: { ...params, ...extra, section },
			});
			return data.status === "success" ? data.result : null;
		},
		[plan?.id, params]
	);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			try {
				setLoading(true);
				const [opts, kpi, filling, manual, month] = await Promise.all([
					fetchSection("filter_options"),
					fetchSection("kpi"),
					fetchSection("filling"),
					fetchSection("manual"),
					fetchSection("month"),
				]);
				if (cancelled) return;
				if (opts) {
					setOptions({
						stores: opts.stores || [],
						statuses: opts.statuses || STATUS_ORDER,
						grades: opts.grades || [],
						city_tiers: opts.city_tiers || [],
						divisions: opts.divisions || [],
						categories: opts.categories || [],
					});
				}
				if (kpi) setSummary(kpi.summary || {});
				if (filling) setFillingRows(filling.rows || []);
				if (manual) {
					setManualRows(manual.rows || []);
					setManualLtlSeason(manual.ltl_season || null);
				}
				if (month) {
					setMonths(month.months || {});
					setLtlMonths(month.ltl_months || []);
				}
			} catch (err) {
				if (!cancelled) showError(err);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [fetchSection, showError]);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			try {
				setLoadingMrp(true);
				const result = await fetchSection("mrp", { group_by: groupBy });
				if (cancelled || !result) return;
				setMrpRows((result.rows || []).map((row, idx) => ({ ...row, _rowId: `${row.group_1 || ""}__${row.group_2 || ""}__${idx}` })));
				setMrpLabels(result.labels || ["Category", ""]);
			} catch (err) {
				if (!cancelled) showError(err);
			} finally {
				if (!cancelled) setLoadingMrp(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [fetchSection, groupBy, showError]);

	const statusCounts = useMemo(
		() => ({
			PENDING: Number(summary.pending_stores) || 0,
			"IN PROGRESS": Number(summary.in_progress_stores) || 0,
			SUBMITTED: Number(summary.submitted_stores) || 0,
			"RM APPROVED": Number(summary.rm_approved_stores) || 0,
			"NSM APPROVED": Number(summary.nsm_approved_stores) || 0,
			"FINAL APPROVED": Number(summary.final_approved_stores) || 0,
		}),
		[summary]
	);

	const kpis = useMemo(() => {
		const totalStores = Number(summary.total_stores) || 0;
		const resolved = Number(summary.resolved_stores) || 0;
		const newQty = Number(summary.new_qty) || 0;
		const ltlQty = Number(summary.ltl_qty) || 0;
		const newNsv = Number(summary.new_nsv) || 0;
		const ltlNsv = Number(summary.ltl_nsv) || 0;
		const newMrp = Number(summary.new_total_mrp) || 0;
		const ltlMrp = Number(summary.ltl_total_mrp) || 0;

		const newAsp = newQty ? round1dp(newNsv / newQty) : 0;
		const ltlAsp = ltlQty ? round1dp(ltlNsv / ltlQty) : 0;
		const newDis = newMrp ? round1dp(((newMrp - newNsv) / newMrp) * 100) : 0;
		const ltlDis = ltlMrp ? round1dp(((ltlMrp - ltlNsv) / ltlMrp) * 100) : 0;

		return {
			totalStores,
			resolved,
			coverage: totalStores ? round1dp((resolved / totalStores) * 100) : 0,
			finalPct: totalStores
				? round1dp((statusCounts["FINAL APPROVED"] / totalStores) * 100)
				: 0,
			newQty,
			newNsv,
			newAsp,
			newDis,
			newOpt: Number(summary.new_opt) || 0,
			qtyGrowth: growthPct(newQty, ltlQty),
			nsvGrowth: growthPct(newNsv, ltlNsv),
			aspGrowth: growthPct(newAsp, ltlAsp),
			disDelta: ltlDis ? round1dp(newDis - ltlDis) : null,
		};
	}, [summary, statusCounts]);

	const monthChart = useMemo(() => {
		const seasonMonths = getSeasonMonths(plan?.season);
		const labels = seasonMonths.map((m) => m.name);
		const ltlByMonth = new Map((ltlMonths || []).map((row) => [Number(row.month), row]));

		const newRevenueByMonth = seasonMonths.map((m) => round1dp(Number(months[`month_${m.key}`]) || 0));
		const totalNewRevenue = newRevenueByMonth.reduce((s, v) => s + v, 0);

		const series = { revenue: { new: [], ltl: [], growth: [] } };

		seasonMonths.forEach((m, idx) => {
			const ltlRow = ltlByMonth.get(m.key) || {};
			const ltlRevenueRaw = Number(ltlRow.nsv) || 0;
			const ltlLakhs = toLakhs(ltlRevenueRaw);

			series.revenue.new.push(newRevenueByMonth[idx]);
			series.revenue.ltl.push(ltlLakhs);
			series.revenue.growth.push(growthPct(newRevenueByMonth[idx], ltlLakhs));
		});

		const totalLtlRevenue = series.revenue.ltl.reduce((s, v) => s + v, 0);
		const hasData =
			totalNewRevenue > 0 || (ltlMonths || []).some((row) => Number(row.nsv) > 0 || Number(row.qty) > 0);

		return {
			labels,
			series,
			hasData,
			overallGrowth: growthPct(totalNewRevenue, totalLtlRevenue),
			total: round1dp(Number(months.total_projection) || 0),
		};
	}, [months, ltlMonths, plan?.season]);

	const mrpChart = useMemo(() => {
		const top = mrpRows.slice(0, 10);
		const labels = top.map((r) =>
			[r.group_1, r.group_2].filter(Boolean).join(" · ").slice(0, 22) || "—"
		);
		return {
			labels,
			series: {
				nsv: { ltl: top.map((r) => toLakhs(r.ltl_nsv)), new: top.map((r) => toLakhs(r.new_nsv)) },
				qty: { ltl: top.map((r) => Number(r.ltl_qty) || 0), new: top.map((r) => Number(r.new_qty) || 0) },
				opt: { ltl: top.map((r) => Number(r.ltl_opt) || 0), new: top.map((r) => Number(r.new_opt) || 0) },
				asp: { ltl: top.map((r) => Number(r.ltl_asp) || 0), new: top.map((r) => Number(r.new_asp) || 0) },
				dis: { ltl: top.map((r) => round1dp(r.ltl_dis)), new: top.map((r) => round1dp(r.new_dis)) },
			},
		};
	}, [mrpRows]);

	const hasFilters = MULTI_FILTER_KEYS.some((key) => (filters[key] || []).length);

	const resetFilters = () =>
		setFilters((prev) => ({
			...prev,
			...MULTI_FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: [] }), {}),
		}));

	const modeHint =
		mode === "FINAL"
			? "Only final approved submissions are counted"
			: mode === "LATEST_SUBMITTED"
			? "Latest submitted or approved submission per store"
			: "Latest submission per store, including drafts";

	return (
		<Box sx={{ height: "100%", minHeight: 0, overflow: "auto", pr: 0.5 }}>
			{/* Filters */}
			<Box sx={{ ...cardSx, p: 1.5, mb: 2 }}>
				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					flexWrap="nowrap"
					sx={{ overflowX: "auto", pb: 0.5 }}
				>
					{filterColumns.map((column) => (
						<Box
							key={column.key}
							sx={{ flex: `${column.width} 1 0px`, minWidth: Math.round(column.width * 0.55) }}
						>
							{column.key === "store_id" ? (
								<StoreMultiSelect
									options={column.option_data.list}
									value={filters.store_id}
									onChange={(keys) => handleFilterChange({ ...filters, store_id: keys })}
									width="100%"
									placeholder="Stores"
								/>
							) : (
								<RenderInputField
									column={column}
									data={filters}
									setData={handleFilterChange}
									width="100%"
									selectOptObjName="option_data"
									disableLabel
								/>
							)}
						</Box>
					))}
					{hasFilters ? (
						<Button
							size="small"
							onClick={resetFilters}
							sx={{ fontSize: 12, fontWeight: 700, flexShrink: 0 }}
						>
							Reset
						</Button>
					) : null}
				</Stack>
				<Typography fontSize={11} color="text.secondary" mt={0.5}>
					{modeHint}
				</Typography>
				{loading ? <LinearProgress sx={{ mt: 1, height: 2, borderRadius: 1 }} /> : null}
			</Box>

			{!loading && kpis.totalStores > 0 && kpis.resolved === 0 ? (
				<Box
					sx={{
						...cardSx,
						p: 1.25,
						mb: 2,
						borderColor: "#ffe0b2",
						bgcolor: "#fff8e1",
					}}
				>
					<Typography fontSize={12.5} color={grey[800]}>
						No submission qualifies under <b>{REPORT_MODES.find((m) => m.key === mode)?.label}</b>{" "}
						for the selected stores. Switch the source to see submitted or draft numbers.
					</Typography>
				</Box>
			) : null}

			{/* KPI strip */}
			<Box
				display="grid"
				gap={1.5}
				mb={2}
				sx={{
					gridTemplateColumns: {
						xs: "repeat(2, minmax(0, 1fr))",
						md: "repeat(3, minmax(0, 1fr))",
						lg: "repeat(6, minmax(0, 1fr))",
					},
				}}
			>
				<KpiTile
					label="Stores"
					value={formatNumber(kpis.totalStores)}
					hint={`${kpis.resolved} with plan data`}
				/>
				<KpiTile
					label="Final approved"
					value={`${kpis.finalPct}%`}
					hint={`${statusCounts["FINAL APPROVED"]} of ${kpis.totalStores}`}
					accent="#2e7d32"
				/>
				<KpiTile
					label="New NSV"
					value={formatLakhs(kpis.newNsv)}
					delta={kpis.nsvGrowth}
					deltaLabel="vs PRV"
				/>
				<KpiTile
					label="New Qty"
					value={formatNumber(kpis.newQty)}
					delta={kpis.qtyGrowth}
					deltaLabel="vs PRV"
				/>
				<KpiTile
					label="New ASP"
					value={formatNumber(kpis.newAsp)}
					delta={kpis.aspGrowth}
					deltaLabel="vs PRV"
				/>
				<KpiTile
					label="New Dis"
					value={`${kpis.newDis}%`}
					delta={kpis.disDelta}
					deltaLabel="pts vs PRV"
				/>
			</Box>

			{/* Section 1 — filling & approval */}
			<SectionCard
				title="Filling & approval progress"
				subtitle="Where every store sits in the approval ladder"
			>
				<Box mb={2}>
					<StatusFunnel counts={statusCounts} total={kpis.totalStores} />
				</Box>
				<Box sx={{ height: 320, border: `1px solid ${grey[200]}`, borderRadius: 1.5, overflow: "hidden" }}>
					<TanstackDatagrid
						pageData={fillingRows}
						setPageData={setFillingRows}
						columns={fillingColumns}
						isLoading={loading}
						rowHeight={38}
						headerHeight={40}
						disablePagination
						serverSidePagination={false}
						getRowId={(row) => row.store_id}
						emptyMessage="No stores match the current filters"
					/>
				</Box>
			</SectionCard>

			{/* Section 2 — manual projection */}
			<SectionCard
				title="Manual projection"
				subtitle={`New target vs derived target vs LTL${
					manualLtlSeason ? ` (${manualLtlSeason})` : ""
				} actuals, and the month-wise revenue plan`}
				actions={
					<Typography fontSize={12} fontWeight={700} color={grey[700]}>
						Season total {monthChart.total}L
					</Typography>
				}
			>
				{monthChart.hasData ? (
					<Box sx={{ mb: 2 }}>
						<Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5} flexWrap="wrap" useFlexGap>
							<Typography sx={sectionLabelSx}>
								Month-wise {MONTH_KPI_OPTIONS.find((o) => o.key === monthKpi)?.label} — LTL vs New
							</Typography>
							<Select
								size="small"
								value={monthKpi}
								onChange={(e) => setMonthKpi(e.target.value)}
								sx={{ fontSize: 12, height: 30, minWidth: 140 }}
							>
								{MONTH_KPI_OPTIONS.map((o) => (
									<MenuItem key={o.key} value={o.key} sx={{ fontSize: 12.5 }}>
										{o.label}
									</MenuItem>
								))}
							</Select>
						</Stack>
						<BarChart
							height={220}
							xAxis={[{ scaleType: "band", data: monthChart.labels }]}
							series={[
								{
									data: monthChart.series[monthKpi].ltl,
									label: `LTL${manualLtlSeason ? ` (${manualLtlSeason})` : ""}`,
									color: "#bdbdbd",
									valueFormatter: (v) => `${v}${MONTH_KPI_UNITS[monthKpi]}`,
								},
								{
									data: monthChart.series[monthKpi].new,
									label: `New${
										monthKpi === "revenue" && monthChart.overallGrowth != null
											? ` (${monthChart.overallGrowth > 0 ? "+" : ""}${monthChart.overallGrowth}%)`
											: ""
									}`,
									color: "#1976d2",
									valueFormatter: (v, { dataIndex }) => {
										const growth =
											monthKpi === "revenue" ? monthChart.series.revenue.growth[dataIndex] : null;
										return `${v}${MONTH_KPI_UNITS[monthKpi]}${
											growth != null ? ` (${growth > 0 ? "+" : ""}${growth}% vs LTL)` : ""
										}`;
									},
								},
							]}
							margin={{ left: 55, right: 16, top: 24, bottom: 32 }}
						/>
					</Box>
				) : null}
				<Box sx={{ height: 320, border: `1px solid ${grey[200]}`, borderRadius: 1.5, overflow: "hidden" }}>
					<TanstackDatagrid
						pageData={manualRows}
						setPageData={setManualRows}
						columns={manualColumns}
						isLoading={loading}
						rowHeight={38}
						headerHeight={40}
						disablePagination
						serverSidePagination={false}
						getRowId={(row) => row.store_id}
						emptyMessage="No stores match the current filters"
					/>
				</Box>
			</SectionCard>

			{/* Section 3 — MRP projection */}
			<SectionCard
				title="MRP projection"
				subtitle="Merchandise rollup from the resolved submissions (disabled MRPs excluded)"
				actions={
					<RenderInputField
						column={MRP_GROUP_COLUMN}
						data={filters}
						setData={handleFilterChange}
						width={MRP_GROUP_COLUMN.width}
						selectOptObjName="option_data"
						disableLabel
					/>
				}
			>
				{mrpChart.labels.length ? (
					<Box sx={{ mb: 2 }}>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							mb={0.5}
							flexWrap="wrap"
							useFlexGap
						>
							<Typography sx={sectionLabelSx}>
								{MRP_KPI_OPTIONS.find((o) => o.key === mrpKpi)?.label} — LTL vs New
							</Typography>
							<Select
								size="small"
								value={mrpKpi}
								onChange={(e) => setMrpKpi(e.target.value)}
								sx={{ fontSize: 12, height: 30, minWidth: 140 }}
							>
								{MRP_KPI_OPTIONS.map((o) => (
									<MenuItem key={o.key} value={o.key} sx={{ fontSize: 12.5 }}>
										{o.label}
									</MenuItem>
								))}
							</Select>
						</Stack>
						<BarChart
							height={300}
							xAxis={[{ scaleType: "band", data: mrpChart.labels }]}
							series={[
								{
									data: mrpChart.series[mrpKpi].ltl,
									label: "LTL",
									color: "#bdbdbd",
									valueFormatter: (v) => `${v}${MRP_KPI_OPTIONS.find((o) => o.key === mrpKpi)?.unit}`,
								},
								{
									data: mrpChart.series[mrpKpi].new,
									label: "New",
									color: "#1976d2",
									valueFormatter: (v) => `${v}${MRP_KPI_OPTIONS.find((o) => o.key === mrpKpi)?.unit}`,
								},
							]}
							margin={{ left: 55, right: 16, top: 24, bottom: 60 }}
						/>
					</Box>
				) : null}
				<Box sx={{ height: 400, border: `1px solid ${grey[200]}`, borderRadius: 1.5, overflow: "hidden" }}>
					<TanstackDatagrid
						pageData={mrpRows}
						setPageData={setMrpRows}
						columns={buildMrpColumns(mrpLabels)}
						isLoading={loadingMrp}
						rowHeight={38}
						headerHeight={40}
						disablePagination
						serverSidePagination={false}
						getRowId={(row) => row._rowId}
						emptyMessage="No MRP data for the current filters"
					/>
				</Box>
			</SectionCard>
		</Box>
	);
};

export default PlanReport;
